<template>
  <main class="p-1 lg:min-w-210">
    <div class="mt-2 flex justify-center">
      <base-picture v-bind="heading.picture" class="w-8 shrink-0 lg:w-12" />
      <h1 class="p-2 display-3">
        {{ heading.title }}
      </h1>
    </div>
    <div class="px-0 container">
      <section v-if="data">
        <vf-tabs
          :active="activeTab"
          class="mt-4 lg:max-w-200"
          :tabs="data.tabs.map(({ category }) => category)"
          variant="left"
          @change="onTabChange"
        >
          <template v-for="(tab, i) in tabs" :key="i" #[`content-${i}`]>
            <p v-if="tab.headline" class="my-8 px-8">
              {{ tab.headline }}
            </p>
            <vf-accordion
              v-for="(item, j) in tab.accordions"
              :key="item.id"
              v-model="item.model"
              v-style:m="{ sm: j === 0 ? '-3px 0 0' : '' }"
              class="py-1 lg:max-w-245 md:pl-8"
              :class="{ 'b-0': !i }"
              :data-scroll-el="item.id"
              size="sm"
              :title="item.label"
              :value="`${j}`"
              variant="aligned"
              @update:model-value="toggleAccordion(item)"
            >
              <template v-for="(sizeTable, k) in item.sizeTables" :key="k">
                <p v-if="sizeTable.specificMeasurements" v-html="sizeTable.specificMeasurements" class="mb-2" />
                <div class="flex between">
                  <div class="justify-start pl-1">
                    <vf-switch
                      v-if="sizeTable.toggle !== undefined"
                      class="my-2"
                      :label-off="measureAbbreviation.in"
                      :label-on="measureAbbreviation.cm"
                      :model-value="sizeTable.toggle"
                      @update:model-value="(v) => sizeTable.toggle = v"
                    />
                  </div>
                  <div class="items-end pr-3 pt-3">
                    <vf-link
                      v-if="sizeTable.measure"
                      :model-value="!measureHow"
                      @click="scrollToElement('howToMeasure')"
                    >
                      {{ measureHow.yourself }}
                    </vf-link>
                  </div>
                </div>
                <p class="my-4 title-3">
                  {{ sizeTable.tableName }}
                </p>
                <div
                  class="mb-10 overflow-y-auto md:max-w-full"
                  style="max-width:80rem"
                  @scroll="isTableScrolling = true"
                  @scrollend="isTableScrolling = false"
                >
                  <vf-table
                    :aria-label="`${heading}: ${item.id}`"
                    class="ws-pre-wrap text-center text-sm"
                  >
                    <tr>
                      <th
                        v-if="!sizeTable.removeHeaderCell"
                        class="sticky left-0 center b b-b-2 b-grey-60 bg-white p-5 align-middle fw-light"
                        scope="col"
                        :style="{
                          'box-shadow': isTableScrolling
                            ? 'inset -3px 0 rgba(0,0,0,0.1), inset 3px 0 rgba(0,0,0,0.1)' : '',
                        }"
                      >
                        <span class="sr-only"> {{ $t.size }}</span>
                      </th>
                      <th
                        v-for="(sizeHeader, n) in sizeTable.sizeHeading"
                        :key="n"
                        class="min-w-26 center b b-b-2 b-grey-60 bg-white px-2 py-5 align-middle fw-light"
                        :class="{ 'sticky left-0': n === 0 && sizeTable.fullHeader }"
                        scope="col"
                        :style="{
                          'box-shadow': n === 0 && sizeTable.fullHeader && isTableScrolling
                            ? 'inset -3px 0 rgba(0,0,0,0.1), inset 3px 0 rgba(0,0,0,0.1)' : '',
                        }"
                      >
                        {{ sizeHeader }}
                      </th>
                    </tr>
                    <tr
                      v-for="(sizes, Size, l) in asObject(sizeTable.sizes)"
                      :key="Size"
                      :class="{ 'bg-grey-90': l % 2 }"
                    >
                      <th
                        v-if="!sizeTable.removeColumn"
                        class="sticky left-0 min-w-25 center b b-grey-60 py-5 fw-light"
                        :class="l % 2 ? 'bg-grey-90' : 'bg-white'"
                        scope="row"
                        :style="{
                          'box-shadow': isTableScrolling
                            ? 'inset -3px 0 rgba(0,0,0,0.1), inset 3px 0 rgba(0,0,0,0.1)' : '',
                        }"
                      >
                        <div>
                          {{ Size }}
                        </div>
                      </th>
                      <td
                        v-for="(size, p) in sizes[sizeTable.toggle ? 'cm' : 'in'] || sizes"
                        :key="p"
                        class="b b-grey-60 py-5"
                      >
                        {{ size }}
                      </td>
                    </tr>
                  </vf-table>
                </div>
              </template>
            </vf-accordion>
            <div v-if="isMeasureVisible">
              <div class="mb-8 b-t b-grey-60 pt-16" data-scroll-el="howToMeasure">
                <h2 class="my-4 display-3 <md:text-center">
                  {{ measureHow.title }}
                </h2>
                <p class="my-4 <md:text-center">
                  {{ measureHow.explore }}
                </p>
                <div class="grid <md:cols-1 lg:cols-3">
                  <div class="<md:mb-6">
                    <base-picture v-bind="tab.measurements.picture" class="shrink-0" />
                    <base-picture v-bind="tab.measurements.picture[`${gender}`]" class="shrink-0" />
                  </div>
                  <div class="grid col-span-2 w-full lg:cols-2">
                    <div v-for="(content, key) in tab.measurements.contents[`${gender}`]" :key>
                      <div class="mb-8 flex">
                        <div class="w-1/4">
                          <div class="flex justify-center">
                            <span class="b-1 px-3 py-1 c-black">
                              {{ content.part }}
                            </span>
                          </div>
                        </div>
                        <div class="w-3/4">
                          <div class="title-4">
                            {{ content.title }}
                          </div>
                          <div class="my-2 text-sm lg:pr-16">
                            <div />
                            <div>
                              {{ content.description }}
                              <p v-html="content.tip" class="mt-4" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="text-center space-y-4">
                  <h3 class="title-3">
                    {{ measureHow.wrongSize }}
                  </h3>
                  <p>
                    {{ measureHow.freeReturns }}
                    <span v-html="measureHow.findOutMore" class="mt-2" />
                  </p>
                </div>
              </div>
            </div>
          </template>
        </vf-tabs>
      </section>
    </div>
  </main>
</template>

<script lang="ts" setup>
const activeTab = ref('0')
const activeAccordion = ref('0')

const categoryQuery = useRouteQuery('category')
const productQuery = useRouteQuery('product')

const isTableScrolling = ref(false)
const isMeasureVisible = ref(false)
const gender = ref('')
const header = useHeaderStore()

const { data, error } = await useApi().internal.getStaticPage('help/product-info/size-charts')
if (!data.value) throw createError({ ...error.value!, fatal: true })
const { heading, tabs, measureAbbreviation, measureHow } = data.value

activeTab.value = `${Math.max(data.value.tabs.findIndex(({ category }) => categoryQuery.value === category), 0)}`
activeAccordion.value
    = `${data.value.tabs[Math.max(+activeTab.value, 0)]?.accordions.findIndex(({ id }) => productQuery.value === id)}`

const measureSectionCheck = (state: boolean, person: string) => {
  isMeasureVisible.value = state
  gender.value = person
}

const toggleAccordion = (item) => {
  if (productQuery.value === item.id) {
    productQuery.value = null
  }
  else {
    productQuery.value = item.id
    measureSectionCheck(item.isMeasureVisible, item.gender)
  }
}

const onTabChange = (active: string) => {
  activeAccordion.value = ''
  productQuery.value = null
  categoryQuery.value = data.value?.tabs[active].category
  isMeasureVisible.value = false
}

const accordion = data.value?.tabs[activeTab.value].accordions[activeAccordion.value]
if (accordion) (accordion.model = activeAccordion.value || '')

const asObject = (a: any) => a as Record<string, any>

onMounted(async () => {
  if (accordion) {
    await wait(1000).then(() => {
      scrollToElement(accordion.id, { offset: header.height.promoBar + header.height.navigation })
      measureSectionCheck(accordion.isMeasureVisible, accordion.gender)
    })
  }
})
</script>
